import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../apiServices/axios';
import {
    INTELLIGENCE_INSIGHTS_V2_GENERATE,
    INTELLIGENCE_INSIGHTS_V2_OVERVIEW,
} from '../apiServices/api_routes';
import { overviewPayloadToViewModel } from '../utils/intelligenceInsightsOverviewViewModel';
import { withInsightsContextQuery } from '../utils/intelligenceInsightsContext';
import {
    getOverviewDummyData,
    OVERVIEW_DEMO_VARIANT,
    OVERVIEW_VARIANTS,
} from '../components/LensIntelligence/IntelligenceInsights/overviewDummyData';

/** Set true only for local Figma-state previews without API. */
export const OVERVIEW_DEMO_MODE = false;

const GENERATE_POLL_MS = 30_000;
const GENERATE_POLL_MAX_MS = 45 * 60 * 1000;

function sleep(ms) {
    return new Promise(resolve => {
        window.setTimeout(resolve, ms);
    });
}

function generationSettled(data, previousVersion) {
    const status = data?.analysis?.status;
    const version = data?.analysis?.version;
    const progress = data?.data?.generation_progress || {};
    const active = Boolean(progress.active);
    const failedVersion = progress.failed_version;
    if (active) return null;
    if (status === 'failed' && version != null && version !== previousVersion) {
        return 'failed';
    }
    if (
        (status === 'complete' || status === 'complete_with_warnings') &&
        (previousVersion == null ? version != null : version !== previousVersion)
    ) {
        return 'complete';
    }
    if (
        failedVersion != null &&
        (previousVersion == null || failedVersion > previousVersion)
    ) {
        return 'failed';
    }
    return null;
}

export function useIntelligenceInsightsOverview(projectId, analysisContext = null) {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [demoVariant, setDemoVariant] = useState(OVERVIEW_DEMO_VARIANT);
    const pollingRef = useRef(false);
    const contextType = analysisContext?.contextType;
    const contextLabel = analysisContext?.contextLabel;
    const workstreamIds = analysisContext?.workstreamIds;
    const workstreamKey = Array.isArray(workstreamIds) ? workstreamIds.join(',') : '';

    const fetchOverview = useCallback(
        async ({ silent = false } = {}) => {
            if (!projectId || OVERVIEW_DEMO_MODE) return null;
            if (!silent) {
                setLoading(true);
                setError(null);
            }
            try {
                const url = withInsightsContextQuery(
                    INTELLIGENCE_INSIGHTS_V2_OVERVIEW(projectId),
                    { contextType, contextLabel, workstreamIds },
                );
                const { data } = await axiosInstance.get(url);
                setPayload(data || null);
                return data || null;
            } catch (err) {
                const detail = err?.response?.data?.detail;
                if (!silent) {
                    setError(
                        typeof detail === 'string'
                            ? detail
                            : 'Could not load Intelligence Insights overview.',
                    );
                    setPayload(null);
                }
                return null;
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [projectId, contextType, contextLabel, workstreamKey, workstreamIds],
    );

    useEffect(() => {
        setPayload(null);
    }, [contextType, workstreamKey]);

    useEffect(() => {
        void fetchOverview();
    }, [fetchOverview]);

    // Resume polling when the page loads mid-generation (e.g. refresh, tab return)
    // or when a background worker is running without this hook's generateAnalysis loop.
    useEffect(() => {
        if (!projectId || OVERVIEW_DEMO_MODE) return undefined;
        if (!payload?.data?.generation_progress?.active) return undefined;
        if (pollingRef.current) return undefined;

        let cancelled = false;
        const baselineVersion = payload?.analysis?.version ?? null;

        async function pollInProgressGeneration() {
            pollingRef.current = true;
            setGenerating(true);
            const started = Date.now();
            try {
                while (!cancelled && Date.now() - started < GENERATE_POLL_MAX_MS) {
                    const data = await fetchOverview({ silent: true });
                    const active = Boolean(data?.data?.generation_progress?.active);
                    const settled = generationSettled(data, baselineVersion);
                    if (settled === 'failed') {
                        const message =
                            data?.data?.generation_progress?.last_error ||
                            data?.data?.conditional_banner?.message ||
                            'Analysis generation failed.';
                        setError(message);
                        break;
                    }
                    if (!active || settled === 'complete') {
                        setError(null);
                        break;
                    }
                    await sleep(GENERATE_POLL_MS);
                }
            } finally {
                pollingRef.current = false;
                setGenerating(false);
            }
        }

        void pollInProgressGeneration();
        return () => {
            cancelled = true;
        };
    }, [projectId, payload?.data?.generation_progress?.active, fetchOverview]);

    const pollUntilSettled = useCallback(
        async previousVersion => {
            const started = Date.now();
            while (Date.now() - started < GENERATE_POLL_MAX_MS) {
                const data = await fetchOverview({ silent: true });
                const settled = generationSettled(data, previousVersion);
                if (settled === 'failed') {
                    const message =
                        data?.data?.generation_progress?.last_error ||
                        data?.data?.conditional_banner?.message ||
                        'Analysis generation failed.';
                    const err = new Error(message);
                    err.response = { data: { detail: message } };
                    throw err;
                }
                if (settled === 'complete') {
                    return data;
                }
                await sleep(GENERATE_POLL_MS);
            }
            const message = 'Analysis generation timed out.';
            const err = new Error(message);
            err.response = { data: { detail: message } };
            throw err;
        },
        [fetchOverview],
    );

    const generateAnalysis = useCallback(
        async ({
            contextType: nextType = contextType || 'entire_project',
            contextLabel: nextLabel = contextLabel || 'Entire Project',
            workstreamIds: nextWorkstreamIds = workstreamIds,
        } = {}) => {
            if (!projectId || OVERVIEW_DEMO_MODE) return null;
            setGenerating(true);
            setError(null);
            pollingRef.current = true;
            const previousVersion = payload?.analysis?.version ?? null;
            try {
                const body = {
                    context_type: nextType,
                    context_label: nextLabel,
                };
                if (Array.isArray(nextWorkstreamIds) && nextWorkstreamIds.length) {
                    body.workstream_ids = nextWorkstreamIds;
                }
                const response = await axiosInstance.post(
                    INTELLIGENCE_INSIGHTS_V2_GENERATE(projectId),
                    body,
                );
                const data = response?.data;
                if (data?.overview) {
                    setPayload(data.overview);
                }
                if (data?.status === 'generating' || response?.status === 202) {
                    return await pollUntilSettled(previousVersion);
                }
                if (!data?.overview) {
                    await fetchOverview();
                }
                return data;
            } catch (err) {
                await fetchOverview({ silent: true });
                throw err;
            } finally {
                pollingRef.current = false;
                setGenerating(false);
            }
        },
        [projectId, fetchOverview, pollUntilSettled, payload, contextType, contextLabel, workstreamIds],
    );

    const overviewData = useMemo(() => {
        if (OVERVIEW_DEMO_MODE) {
            return getOverviewDummyData(demoVariant);
        }
        const vm = overviewPayloadToViewModel(payload);
        if (vm) return vm;
        // Safe empty state while loading / before first paint.
        return {
            variant: OVERVIEW_VARIANTS.NOT_GENERATED,
            hasAnalysis: false,
            metaLine: 'Loading…',
            primaryActionLabel: 'Generate analysis',
            modules: [],
            shellSources: { ready: 0, processing: 0 },
        };
    }, [payload, demoVariant]);

    return {
        overviewData,
        payload,
        loading,
        generating,
        error,
        fetchOverview,
        generateAnalysis,
        demoVariant,
        setDemoVariant,
        demoMode: OVERVIEW_DEMO_MODE,
    };
}

export default useIntelligenceInsightsOverview;
